import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { pickLocale, mapsUrl, propertyMapsUrl } from "@/lib/helpers";
import { useSettings } from "@/context/SettingsContext";
import { track } from "@/api/analytics";
import * as Icons from "lucide-react";

/**
 * Guest map view. Lightweight by design (no embedded map to avoid API keys).
 * POIs are grouped by category to help scanability.
 */
export default function MapPage() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [pois, setPois] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/public/pois"),
      api.get("/public/categories"),
    ])
      .then(([pRes, cRes]) => {
        // Include any POI Google Maps can resolve: with coordinates OR a written address.
        const list = (pRes.data || []).filter((p) => (p.lat && p.lng) || p.address);
        setPois(list);
        setCategories(cRes.data || []);
      })
      .catch(() => {
        // Leave the empty state; the boutique layout still renders correctly.
      });
  }, []);

  const propHref = propertyMapsUrl(settings);

  /** Group POIs by category, preserving each category's declared order. */
  const groups = useMemo(() => {
    const catOrder = new Map(categories.map((c, i) => [c.id, i]));
    const byCat = new Map();
    for (const p of pois) {
      const arr = byCat.get(p.category_id) || [];
      arr.push(p);
      byCat.set(p.category_id, arr);
    }
    const result = [];
    for (const c of categories) {
      const items = byCat.get(c.id);
      if (items?.length) result.push({ category: c, items });
    }
    // Any orphan POIs (missing category) get appended at the end
    for (const [cid, items] of byCat.entries()) {
      if (!catOrder.has(cid)) result.push({ category: null, items });
    }
    return result;
  }, [pois, categories]);

  return (
    <div className="px-5 pt-6 pb-8">
      <div className="text-center animate-fade-up">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-bronze-50 flex items-center justify-center">
          <Icons.Map strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
        </div>
        <h1 data-testid="map-title" className="font-editorial italic text-[38px] text-ink-900 mt-3">
          {t("map.title")}
        </h1>
        <p
          data-testid="map-tagline"
          className="font-editorial italic text-[15px] text-ink-500 mt-1"
        >
          {t("tagline.map")}
        </p>
        <div className="flex justify-center my-3"><span className="ornament" /></div>
      </div>

      {/* "You are here" — the property, given extra visual weight */}
      {propHref && (
        <a
          href={propHref}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("click_maps", { target_type: "property" })}
          className="mt-2 flex items-center gap-3 rounded-2xl border border-bronze-200/70 bg-bronze-50/70 px-4 py-4 hover:bg-bronze-50 transition"
          data-testid="map-property-link"
        >
          <div className="w-14 h-14 rounded-2xl bg-bronze-100 flex items-center justify-center shrink-0">
            <Icons.Home strokeWidth={1.4} className="w-6 h-6 text-bronze-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-bronze-500">
              {t("map.you_are_here")}
            </div>
            <div className="text-sm font-semibold text-ink-900 truncate">{settings?.site_name}</div>
            {settings?.contact_address && (
              <div className="text-xs text-ink-500 truncate">{settings.contact_address}</div>
            )}
          </div>
          <Icons.ArrowUpRight strokeWidth={1.4} className="w-4 h-4 text-ink-300 shrink-0" />
        </a>
      )}

      {/* POIs grouped by category */}
      <div className="mt-6 flex flex-col gap-5">
        {groups.map(({ category, items }, gi) => (
          <section key={category?.id || `orphan-${gi}`} data-testid={`map-group-${category?.slug || "other"}`}>
            {category && (
              <div className="flex items-center gap-2 mb-2 px-1">
                {(() => {
                  const CIcon = Icons[category.icon] || Icons.MapPin;
                  return <CIcon strokeWidth={1.4} className="w-3.5 h-3.5 text-bronze-500" />;
                })()}
                <h2 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-bronze-500">
                  {pickLocale(category.name, i18n.language)}
                </h2>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {items.map((p) => (
                <a
                  key={p.id}
                  href={mapsUrl({ lat: p.lat, lng: p.lng, address: p.address })}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => track("click_maps", { target_type: "poi", target_id: p.id })}
                  data-testid={`map-poi-${p.id}`}
                  className="flex items-center gap-3 rounded-xl border border-ink-200/40 bg-white/95 px-4 py-3 hover:bg-cream-100 active:scale-[0.99] transition"
                >
                  <Icons.MapPin strokeWidth={1.4} className="w-4 h-4 text-corten-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-900 truncate">{pickLocale(p.title, i18n.language)}</div>
                    {p.address && <div className="text-xs text-ink-300 truncate">{p.address}</div>}
                  </div>
                  <Icons.ArrowUpRight strokeWidth={1.4} className="w-4 h-4 text-ink-300" />
                </a>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
